import { Dropdown, DropdownItem, Search } from 'platform/components';
import { Show } from 'platform/foundation';
import { startTransition, useState } from 'react';
import { TestIdProps, suffixTestId } from 'shared/web';
import { SMART_SEARCH_KEY } from '../../../../constants/smartSearchKey';
import { useSmartSearchFilter } from '../../../../store/selectors/useSmartSearchFilter';
import { useEagGridStore } from '../../../../store/useEagGridStore';
import { useSmartSearchHistory } from './hooks/useSmartSearchHistory';
const ENTER_KEY = 'Enter';
const ESCAPE_KEY = 'Escape';
export type QuickFilterInputProps = {
  initialValue?: string;
  onSubmit?: () => void;
  onEscape?: () => void;
  className?: string;
} & TestIdProps;
export function QuickFilterInput(props: QuickFilterInputProps) {
  // Fine-grained selector — only re-renders when the smart search value changes,
  // not on every filter change (column filters, etc.).
  const smartSearchValue = useSmartSearchFilter();
  // Stable store reference for reading latest filterValues in event handlers (no subscription).
  const store = useEagGridStore();
  const history = useSmartSearchHistory();
  const [localValue, setLocalValue] = useState<string>(props.initialValue ?? smartSearchValue);
  const [prevSmartSearch, setPrevSmartSearch] = useState(smartSearchValue);

  /**
   * In-render state adjustment: sync external store value to local input state.
   * This replaces a useEffect + setState pattern. React handles setState during
   * render as a synchronous bail-out — the component re-renders immediately with
   * the new value before committing to the DOM, avoiding the double-render cycle
   * that the useEffect approach causes.
   *
   * @see https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
   */
  if (prevSmartSearch !== smartSearchValue) {
    setPrevSmartSearch(smartSearchValue);
    setLocalValue(smartSearchValue);
  }
  const submitSearch = (value: string) => {
    const currentFilters = store.getState().filterValues;
    const nextFilters = {
      ...currentFilters
    };
    if (value) {
      nextFilters[SMART_SEARCH_KEY] = {
        [SMART_SEARCH_KEY]: value
      };
    } else {
      delete nextFilters[SMART_SEARCH_KEY];
    }

    // Non-urgent: wrap in startTransition so the input stays responsive during
    // the grid re-render triggered by the filter change (rerender-transitions).
    startTransition(() => {
      store.setFilterValues(nextFilters);
    });
    history.refetch();
  };
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === ENTER_KEY) {
      event.preventDefault();
      history.closeMenu();
      submitSearch(localValue);
      props.onSubmit?.();
    } else if (event.key === ESCAPE_KEY) {
      history.closeMenu();
      props.onEscape?.();
    }
  };
  const handleChange = (value: string | null) => {
    setLocalValue(value ?? '');
    if (!history.menuOpen) {
      history.openMenu();
    }
  };
  const handleClear = () => {
    setLocalValue('');
    submitSearch('');
  };
  const handleFocus = () => {
    history.openMenu();
  };
  const handleOptionClick = (value: string) => {
    setLocalValue(value);
    history.closeMenu();
    submitSearch(value);
  };
  return <div onKeyDown={handleKeyDown} className={props.className ?? 'w-full min-w-0'}>
      <Search value={localValue} onChange={handleChange} onClear={handleClear} onFocus={handleFocus} data-testid={suffixTestId('quickFilter', props)} />
      <Show when={history.searchOptions?.length}>
        <Dropdown dropdownControl={<div />} isOpen={history.menuOpen} onClose={history.closeMenu} strategy="fixed" isFocusManagementDisabled data-testid={suffixTestId('quickFilterDropdown', props)}>
          {history.searchOptions?.map(option => <DropdownItem key={option.value} label={option.label} onClick={() => handleOptionClick(option.value)} />)}
        </Dropdown>
      </Show>
    </div>;
}